// ─────────────────────────────────────────────────────────────────────────────
// PerformanceAnalytics — Phase 8 Performance tab maturation
//
// Replaces the two-chart "Charts" tab with a proper performance dashboard.
// Answers: "How am I doing, and is it getting better or worse?"
//
// Sections:
//   1. KPI deep-dive — expectancy, profit factor, largest win/loss, best month
//   2. Equity curve + drawdown (separated for readability)
//   3. Rolling win rate (20-trade window) — is your edge stable?
//   4. P&L distribution histogram — consistency + fat tails
//   5. Monthly P&L bar chart
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from "react";
import {
  Area, AreaChart, Bar, BarChart, Cell,
  Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";

function tradeNet(t) {
  return (Number(t.pnl) || 0) - (Number(t.fees) || 0) - (Number(t.commissions) || 0);
}

// ─── KPI tile ─────────────────────────────────────────────────────────────────
function KpiTile({ label, value, color, sub, good, bad }) {
  // good/bad are thresholds for contextual coloring
  const resolvedColor = color || (
    good != null && Number(value) >= good ? C.green :
    bad  != null && Number(value) <= bad  ? C.red   : C.text
  );
  return (
    <div style={{
      padding: "14px 16px",
      background: "#1a1d2e",
      border: `1px solid ${C.border}`,
      borderRadius: 10,
    }}>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: resolvedColor, fontFamily: "monospace" }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Rolling win rate ─────────────────────────────────────────────────────────
function buildRollingWinRate(trades, window = 20) {
  const sorted = [...trades]
    .filter(t => t.result === "Win" || t.result === "Loss")
    .sort((a, b) => (a.trade_date || "").localeCompare(b.trade_date || ""));

  if (sorted.length < window) return [];

  const points = [];
  for (let i = window - 1; i < sorted.length; i++) {
    const slice = sorted.slice(i - window + 1, i + 1);
    const wins  = slice.filter(t => t.result === "Win").length;
    points.push({
      date: sorted[i].trade_date?.slice(5) || "",
      wr:   Math.round((wins / window) * 100),
      i:    i,
    });
  }
  return points;
}

// ─── P&L distribution histogram ───────────────────────────────────────────────
function buildDistribution(trades, bucketCount = 20) {
  const nets = trades.map(tradeNet).filter(v => !isNaN(v));
  if (!nets.length) return [];

  const min = Math.min(...nets);
  const max = Math.max(...nets);
  if (min === max) return [{ label: fmt$(min), count: nets.length, value: min }];

  const bucketSize = (max - min) / bucketCount;
  const buckets = Array.from({ length: bucketCount }, (_, i) => ({
    lo: min + i * bucketSize,
    hi: min + (i + 1) * bucketSize,
    count: 0,
  }));

  for (const net of nets) {
    const idx = Math.min(bucketCount - 1, Math.floor((net - min) / bucketSize));
    buckets[idx].count++;
  }

  return buckets.map(b => ({
    label: fmt$(b.lo + (b.hi - b.lo) / 2),
    count:  b.count,
    value:  b.lo + (b.hi - b.lo) / 2,
  }));
}


// ─────────────────────────────────────────────────────────────────────────────
// PerformanceAnalytics — main export
// ─────────────────────────────────────────────────────────────────────────────
export function PerformanceAnalytics({ trades = [], analytics, monthlyData = [], pnlMode = "net" }) {
  // Rolling win rate (20-trade window)
  const rollingWR = useMemo(() => buildRollingWinRate(trades), [trades]);

  // P&L distribution
  const distribution = useMemo(() => buildDistribution(trades), [trades]);

  // Deep KPIs not in the header strip
  const deepKpis = useMemo(() => {
    const nets = trades.map(tradeNet).filter(v => !isNaN(v) && v !== 0);
    if (!nets.length) return null;

    const wins   = nets.filter(v => v > 0);
    const losses = nets.filter(v => v < 0);

    const expectancy = nets.reduce((s, v) => s + v, 0) / nets.length;
    const largestWin  = wins.length   ? Math.max(...wins)          : 0;
    const largestLoss = losses.length ? Math.min(...losses)        : 0;

    // Win/loss ratio (avg win ÷ avg loss)
    const avgWin  = wins.length   ? wins.reduce((s, v) => s + v, 0) / wins.length         : 0;
    const avgLoss = losses.length ? Math.abs(losses.reduce((s, v) => s + v, 0) / losses.length) : 0;
    const winLossRatio = avgLoss > 0 ? avgWin / avgLoss : null;

    // Best and worst month from monthlyData
    const sorted = [...monthlyData].sort((a, b) => b.pnl - a.pnl);
    const bestMonth  = sorted[0];
    const worstMonth = sorted[sorted.length - 1];

    // Consistency: % of months profitable
    const profitableMonths = monthlyData.filter(m => m.pnl > 0).length;
    const monthlyConsistency = monthlyData.length > 0
      ? Math.round((profitableMonths / monthlyData.length) * 100)
      : null;

    // Max drawdown from equityCurve
    const maxDD = analytics.drawdown?.length
      ? Math.min(...analytics.drawdown.map(p => p.v))
      : 0;

    return {
      expectancy, largestWin, largestLoss, winLossRatio,
      bestMonth, worstMonth, monthlyConsistency, maxDD,
      avgWin, avgLoss,
    };
  }, [trades, monthlyData, analytics]);

  if (!trades.length) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
        Log some trades to see your performance analytics.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* ── 1. KPI deep-dive ───────────────────────────────────────────────── */}
      {deepKpis && (
        <Card>
          <SectionTitle>Performance Metrics</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <KpiTile
              label="Expectancy"
              value={fmt$(deepKpis.expectancy)}
              color={deepKpis.expectancy > 0 ? C.green : C.red}
              sub="avg net P&L per trade"
            />
            <KpiTile
              label="Win/Loss Ratio"
              value={deepKpis.winLossRatio != null ? `${deepKpis.winLossRatio.toFixed(2)}x` : "—"}
              color={deepKpis.winLossRatio != null && deepKpis.winLossRatio >= 1.5 ? C.green : deepKpis.winLossRatio != null && deepKpis.winLossRatio < 1 ? C.red : C.yellow}
              sub="avg win ÷ avg loss"
            />
            <KpiTile
              label="Profit Factor"
              value={analytics.profitFactor.toFixed(2)}
              color={analytics.profitFactor >= 1.5 ? C.green : analytics.profitFactor < 1 ? C.red : C.yellow}
              sub={analytics.profitFactor >= 1.5 ? "strong edge" : analytics.profitFactor >= 1 ? "marginal edge" : "losing edge"}
            />
            <KpiTile
              label="Max Drawdown"
              value={fmt$(deepKpis.maxDD)}
              color={deepKpis.maxDD < -1000 ? C.red : deepKpis.maxDD < -500 ? C.yellow : C.green}
              sub="from peak equity"
            />
            <KpiTile
              label="Largest Win"
              value={fmt$(deepKpis.largestWin)}
              color={C.green}
              sub="single trade"
            />
            <KpiTile
              label="Largest Loss"
              value={fmt$(deepKpis.largestLoss)}
              color={C.red}
              sub="single trade"
            />
            {deepKpis.monthlyConsistency != null && (
              <KpiTile
                label="Monthly Consistency"
                value={`${deepKpis.monthlyConsistency}%`}
                color={deepKpis.monthlyConsistency >= 70 ? C.green : deepKpis.monthlyConsistency >= 50 ? C.yellow : C.red}
                sub="profitable months"
              />
            )}
            {deepKpis.bestMonth && (
              <KpiTile
                label="Best Month"
                value={fmt$(deepKpis.bestMonth.pnl)}
                color={C.green}
                sub={deepKpis.bestMonth.month}
              />
            )}
          </div>
        </Card>
      )}

      {/* ── 2. Equity curve ────────────────────────────────────────────────── */}
      <Card>
        <SectionTitle>Equity Curve</SectionTitle>
        {analytics.equityCurve.length < 2 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 30 }}>Not enough trades yet.</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={analytics.equityCurve} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.green} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.muted }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: C.muted }} tickFormatter={v => fmt$(v)} width={60} />
                <Tooltip formatter={v => [fmt$(v), "Equity"]} contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }} />
                <ReferenceLine y={0} stroke={C.border} strokeWidth={1} />
                <Area type="monotone" dataKey="v" stroke={C.green} strokeWidth={2} fill="url(#colorEquity)" />
              </AreaChart>
            </ResponsiveContainer>

            {/* Drawdown chart */}
            {analytics.drawdown?.length > 1 && (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 16, marginBottom: 8 }}>
                  Drawdown from peak
                </div>
                <ResponsiveContainer width="100%" height={100}>
                  <AreaChart data={analytics.drawdown} margin={{ top: 0, right: 4, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorDD" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={C.red} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={C.red} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: C.muted }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 9, fill: C.muted }} tickFormatter={v => fmt$(v)} width={60} />
                    <Tooltip formatter={v => [fmt$(v), "Drawdown"]} contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }} />
                    <ReferenceLine y={0} stroke={C.border} />
                    <Area type="monotone" dataKey="v" stroke={C.red} strokeWidth={1.5} fill="url(#colorDD)" />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
          </>
        )}
      </Card>

      {/* ── 3. Rolling win rate ─────────────────────────────────────────────── */}
      <Card>
        <SectionTitle>Rolling Win Rate (20-trade window)</SectionTitle>
        {rollingWR.length < 2 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 30 }}>
            Need 20+ decisive trades (Win/Loss) to compute rolling win rate.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Each point = win rate of the most recent 20 trades at that moment.
              A flat line means consistent edge. Rising = improving. Falling = edge decaying.
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={rollingWR} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.muted }} interval="preserveStartEnd" />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: C.muted }}
                  tickFormatter={v => `${v}%`}
                  width={40}
                />
                <Tooltip formatter={v => [`${v}%`, "Win Rate"]} contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }} />
                <ReferenceLine y={50} stroke={C.border} strokeDasharray="4 4" label={{ value: "50%", position: "right", fill: C.muted, fontSize: 10 }} />
                <ReferenceLine y={analytics.winRate * 100} stroke={C.blue} strokeDasharray="3 3"
                  label={{ value: `Avg ${Math.round(analytics.winRate * 100)}%`, position: "right", fill: C.blue, fontSize: 10 }} />
                <Line
                  type="monotone"
                  dataKey="wr"
                  stroke={C.green}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>

      {/* ── 4. P&L distribution ─────────────────────────────────────────────── */}
      <Card>
        <SectionTitle>P&L Distribution</SectionTitle>
        {distribution.length < 3 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 30 }}>Not enough trades yet.</div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Frequency of each P&L outcome. Ideal shape: tall bar near your avg win,
              short bars on large losses. Large outlier loss bars = fat tail risk.
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={distribution} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.muted }} interval={Math.floor(distribution.length / 6)} />
                <YAxis tick={{ fontSize: 10, fill: C.muted }} width={30} />
                <Tooltip formatter={(v, n, p) => [v + " trades", p.payload.label]} contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }} />
                <ReferenceLine x={fmt$(0)} stroke={C.border} />
                <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                  {distribution.map((entry, i) => (
                    <Cell key={i} fill={entry.value >= 0 ? C.green + "bb" : C.red + "bb"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Card>

      {/* ── 5. Monthly P&L ─────────────────────────────────────────────────── */}
      <Card>
        <SectionTitle>Monthly P&L</SectionTitle>
        {monthlyData.length === 0 ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 30 }}>No monthly data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fill: C.muted, fontSize: 11 }} />
              <YAxis tick={{ fill: C.muted, fontSize: 11 }} tickFormatter={v => fmt$(v)} width={60} />
              <Tooltip formatter={v => [fmt$(v), "P&L"]} contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11 }} />
              <ReferenceLine y={0} stroke={C.border} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                {monthlyData.map((entry, i) => (
                  <Cell key={i} fill={entry.pnl >= 0 ? C.green : C.red} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

    </div>
  );
}
